import { router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text } from 'react-native';

import { AssociationSubPage } from '@/components/accounting/association-sub-page';
import { ColumnPicker } from '@/components/reports/column-picker';
import { ReportActionBar } from '@/components/reports/report-action-bar';
import { buildTableColumns } from '@/components/reports/report-columns';
import { DataTable } from '@/components/ui/data-table';
import { useBackTo } from '@/hooks/use-back-to';
import { useColumnLayout } from '@/hooks/use-column-layout';
import { useColumnPresets } from '@/hooks/use-column-presets';
import type { ReportColumn, ReportDefinition, ReportExportColumn } from '@/types/report-definition';
import { type ExportColumn, exportRowsToCsv, exportRowsToXlsx } from '@/utils/accounting-export';
import { openReportPrintWindow, type PrintColumn } from '@/utils/report-print';

export interface ReportViewProps<TRow, TExportRow = TRow> {
	/** Report config — columns, drill-down, print URL, etc. */
	definition: ReportDefinition<TRow, TExportRow>;
	/** Rows to display. Filtering happens in the calling page. */
	rows: TRow[];
	/** Association the report is scoped to — drives AssociationSubPage chrome. */
	associationId: string;
	/** Association name for the back-link label. */
	associationName?: string;
	/** Render `progressPending` indicator on the DataTable while true. */
	loading?: boolean;
	/** Display above the empty-state if non-null. */
	error?: Error | null;
	/** Slot for the page's filter UI — date pickers, search, etc. Renders above the action bar. */
	filters?: React.ReactNode;
	/** Buttons to render alongside Customize / Print / Excel (e.g. "New entry"). */
	extraActions?: React.ReactNode;
	/** Optional row below the DataTable — used for "Load more" pagination. */
	footerExtras?: React.ReactNode;
	/** Optional params plumbed through `definition.printUrl(associationId, params)`. */
	printParams?: Record<string, unknown>;
	/** Override the default xlsx filename. Extension `.xlsx` is appended if missing. */
	excelFilename?: string;
	/** Empty-state message shown when `rows.length === 0` and we're not loading. */
	emptyMessage?: string;
	/** Rows per page for the in-memory DataTable pagination. Defaults to 50. */
	paginationPerPage?: number;
	/** Initial page (1-based) on mount — lets the caller restore a page from the URL. */
	paginationDefaultPage?: number;
	/** Notified when the user changes page; use it to persist the page (e.g. to the URL). */
	onChangePage?: (page: number) => void;
	/**
	 * Row-click handler. When provided, takes precedence over
	 * `definition.drillTo` — use this when the click should open a modal,
	 * trigger a side-effect, or behave conditionally per row (return early
	 * from inside the handler to make a row inert).
	 */
	onRowClick?: (row: TRow) => void;
	/**
	 * Invoked when a row whose `definition.drillTo` returns `null` is clicked.
	 * Use it to explain why the row isn't drillable (e.g. a synthesized plug
	 * row) instead of letting the click do nothing. Ignored when `onRowClick`
	 * is set (that handler already owns every row).
	 */
	onNonDrillableRowClick?: (row: TRow) => void;
	/**
	 * Per-row style overrides forwarded to the DataTable. Used by grouped reports
	 * (e.g. GL Report) to band header / subtotal rows. Each entry styles every
	 * row for which `when(row)` is true.
	 */
	conditionalRowStyles?: { when: (row: TRow) => boolean; style: object }[];
	debugSqlPath?: string;
	/**
	 * Opt-in server-side sorting. When set, the DataTable hands sort clicks to
	 * `onSort` (column id + direction) instead of sorting the in-memory page
	 * locally — required for cursor/offset-paginated reports where the client
	 * holds only a subset of rows (issue #175). Reports that load their whole
	 * result set in one request omit this and keep the default client sort.
	 */
	serverSort?: {
		field?: string;
		asc?: boolean;
		onSort: (columnId: string, direction: 'asc' | 'desc') => void;
	};
}

/**
 * Generic in-app report view. Wraps `AssociationSubPage` and renders the
 * standard three-button action bar (Customize · Print · Excel) plus a
 * `DataTable` driven by the report's `ReportDefinition.columns`. Column
 * visibility + order persist to localStorage keyed by `definition.id`.
 *
 * Pages provide a `ReportDefinition`, the loaded `rows`, and any
 * report-specific filter UI via the `filters` slot.
 */
export const ReportView = <TRow, TExportRow = TRow>(props: ReportViewProps<TRow, TExportRow>): React.ReactElement => {
	const {
		definition,
		rows,
		associationId,
		associationName,
		loading = false,
		error,
		filters,
		extraActions,
		footerExtras,
		printParams,
		excelFilename,
		emptyMessage = 'No data matches your filters.',
		paginationPerPage = 50,
		paginationDefaultPage,
		onChangePage,
		onRowClick,
		onNonDrillableRowClick,
		conditionalRowStyles,
		debugSqlPath,
		serverSort,
	} = props;

	const [pickerVisible, setPickerVisible] = useState(false);
	const { withBackTo } = useBackTo();

	// Visibility / order is keyed off the definition id — survives across
	// page mounts and (per `useColumnLayout`) tolerates renamed/removed columns.
	const allColumnIds = useMemo(
		() => definition.columns.filter((c) => !c.exportOnly).map((c) => c.id),
		[definition.columns],
	);
	const defaultHiddenIds = useMemo(
		() => definition.columns
			.filter((c) => !c.exportOnly && c.defaultVisible === false)
			.map((c) => c.id),
		[definition.columns],
	);
	const { layout, setLayout, reset: resetLayout } = useColumnLayout({
		storageKey: `reports:${definition.id}:columnLayout:${definition.columnLayoutVersion ?? 'v1'}`,
		allIds: allColumnIds,
		defaultHiddenIds,
	});
	// Presets share the layout's version, not a fixed 'v1'. A preset is a saved
	// list of column IDs, so it goes stale for exactly the same reason a layout
	// does: when `columnLayoutVersion` bumps, the old IDs may no longer exist or
	// may no longer be the intended default set. Pinning presets to 'v1' let a
	// v2-era GL preset restore the retired "Num" column and hide the new "#",
	// silently undoing the version bump through the other persistence path.
	const { presets, savePreset, deletePreset } = useColumnPresets({
		storageKey: `reports:${definition.id}:columnPresets:${definition.columnLayoutVersion ?? 'v1'}`,
	});

	const columnById = useMemo(() => {
		const map = new Map<string, ReportColumn<TRow>>();
		for (const c of definition.columns) map.set(c.id, c);
		return map;
	}, [definition.columns]);

	// On-screen columns: ordered by layout.visible, dropping unknown / exportOnly.
	const visibleReportColumns = useMemo<ReportColumn<TRow>[]>(
		() => layout.visible
			.map((id) => columnById.get(id))
			.filter((c): c is ReportColumn<TRow> => Boolean(c) && !c!.exportOnly),
		[layout.visible, columnById],
	);

	const tableColumns = useMemo(
		() => buildTableColumns(visibleReportColumns),
		[visibleReportColumns],
	);

	// Column choices for the picker — exclude exportOnly (those aren't user-toggleable).
	const columnChoices = useMemo(
		() => definition.columns
			.filter((c) => !c.exportOnly)
			.map((c) => ({ id: c.id, label: c.header })),
		[definition.columns],
	);

	// Default flat export = visible columns (in user-chosen order) + any
	// exportOnly columns appended at the end. exportOnly columns always export
	// regardless of the on-screen visibility layout.
	const buildDefaultExport = (): { columns: ReportExportColumn<TRow>[]; rows: TRow[] } => {
		const visibleSet = new Set(visibleReportColumns.map((c) => c.id));
		const cols = [
			...visibleReportColumns,
			...definition.columns.filter((c) => c.exportOnly && !visibleSet.has(c.id)),
		].map((c) => toExportColumn(c));
		return { columns: cols, rows };
	};

	// `buildExport` lets a report flatten one summary row into many detail
	// rows (Journal: one row per line, not per entry). Resolved here so the
	// three handlers (Excel / CSV / print fallback) share one shape.
	//
	// The cast on the default branch is sound: `TExportRow` defaults to `TRow`
	// on `ReportDefinition`, so when `buildExport` is undefined the report
	// hasn't widened the export shape and the default's `TRow` columns/rows
	// are by construction the same type as `TExportRow`. We can't prove that
	// statically without conditional types, hence the boundary cast.
	type ResolvedExport = { columns: ReportExportColumn<TExportRow>[]; rows: TExportRow[] };
	const resolvedExport: ResolvedExport = definition.buildExport
		? definition.buildExport(rows)
		: (buildDefaultExport() as unknown as ResolvedExport);

	const exportFilename = excelFilename ?? `${definition.id}-${Date.now()}`;

	const toExcelColumns = (cols: ReportExportColumn<TExportRow>[]): ExportColumn<TExportRow>[] =>
		cols.map((c) => ({ header: c.header, value: c.accessor, format: c.format, width: c.width }));

	const onExcel = async () => {
		await exportRowsToXlsx(exportFilename, toExcelColumns(resolvedExport.columns), resolvedExport.rows);
	};

	const onCsv = async () => {
		await exportRowsToCsv(exportFilename, toExcelColumns(resolvedExport.columns), resolvedExport.rows);
	};

	const onPrint = () => {
		// Prefer the definition's dedicated print route (some reports — Balance
		// Sheet, P&L, A/R Aging Detail — have hand-tuned print surfaces that the
		// generic table-print fallback can't reproduce).
		if (Platform.OS === 'web' && definition.printUrl) {
			const url = definition.printUrl(associationId, printParams ?? {});
			window.open(url, '_blank');
			return;
		}
		const printCols: PrintColumn<TExportRow>[] = resolvedExport.columns.map((c) => ({
			header: c.header,
			accessor: c.accessor,
			format: c.format,
			align: c.align,
			width: c.width,
		}));
		openReportPrintWindow({
			title: definition.title,
			subtitle: definition.subtitle,
			associationName,
			summary: `${resolvedExport.rows.length.toLocaleString()} row${resolvedExport.rows.length === 1 ? '' : 's'}`,
			columns: printCols,
			rows: resolvedExport.rows,
		});
	};

	// Row-click resolution: explicit `onRowClick` prop wins (used for modals or
	// per-row side effects), otherwise fall back to `definition.drillTo` which
	// navigates through `withBackTo` so the destination's back link returns
	// here.
	const onRowClicked: ((row: TRow) => void) | undefined = onRowClick
		?? (definition.drillTo
			? (row: TRow) => {
				const href = definition.drillTo!(row);
				// A null target means this row isn't drillable (e.g. a synthesized
				// plug row with no underlying GL). Hand it to the explainer
				// callback if provided, rather than routing to a filter that
				// matches no data.
				if (!href) { onNonDrillableRowClick?.(row); return; }
				router.push(typeof href === 'string' ? withBackTo(href) : href);
			}
			: undefined);

	const actionBar = (
		<>
			{extraActions}
			<ReportActionBar
				onCustomize={() => setPickerVisible(true)}
				onPrint={onPrint}
				onExcel={onExcel}
				onCsv={onCsv}
				disabled={loading || rows.length === 0}
				debugSqlPath={debugSqlPath}
				debugReportTitle={definition.title}
			/>
		</>
	);

	return (
		<AssociationSubPage
			associationId={associationId}
			associationName={associationName}
			title={definition.title}
			subtitle={definition.subtitle}
			right={actionBar}
		>
			{filters}
			{error ? (
				<Text style={styles.warn}>Failed to load report: {error.message}</Text>
			) : (
				<>
					<DataTable
						columns={tableColumns}
						data={rows}
						striped
						conditionalRowStyles={conditionalRowStyles}
						highlightOnHover
						pointerOnHover={Boolean(onRowClicked)}
						onRowClicked={onRowClicked}
						expandableRows={Boolean(definition.expandableRowsComponent)}
						expandableRowsComponent={definition.expandableRowsComponent}
						expandOnRowClicked={Boolean(definition.expandableRowsComponent) && !onRowClicked}
						progressPending={loading}
						noDataComponent={<Text style={styles.empty}>{emptyMessage}</Text>}
						pagination
						paginationPerPage={paginationPerPage}
						paginationDefaultPage={paginationDefaultPage}
						onChangePage={onChangePage}
						paginationRowsPerPageOptions={[25, 50, 100, 250]}
						sortServer={serverSort ? true : undefined}
						defaultSortFieldId={serverSort?.field}
						defaultSortAsc={serverSort ? serverSort.asc !== false : undefined}
						onSort={serverSort
							? (column, sortDirection) => serverSort.onSort(String(column?.id ?? ''), sortDirection)
							: undefined}
					/>
					{loading && rows.length > 0 && <ActivityIndicator style={{ marginTop: 8 }} />}
					{footerExtras}
				</>
			)}
			<ColumnPicker
				visible={pickerVisible}
				onDismiss={() => setPickerVisible(false)}
				columns={columnChoices}
				layout={layout}
				onApply={setLayout}
				onReset={resetLayout}
				presets={presets}
				onSavePreset={savePreset}
				onDeletePreset={deletePreset}
			/>
		</AssociationSubPage>
	);
};

function toExportColumn<TRow>(c: ReportColumn<TRow>): ReportExportColumn<TRow> {
	return {
		header: c.header,
		accessor: c.accessor,
		format: c.format,
		align: c.align,
		width: c.width,
	};
}

const styles = StyleSheet.create({
	empty: { color: '#888', fontStyle: 'italic', textAlign: 'center', padding: 24 },
	warn: { color: '#c62828', fontSize: 13 },
});
